import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Sparkles } from "lucide-react";
import Reveal from "@/components/layout/Reveal";

export const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#0B132B]">
      {/* Background uploaded hero — dusk Kiwi home with NeoPower on right */}
      <div className="absolute inset-0">
        <img
          src="/hero/home_hero.png"
          alt="Modern Kiwi dusk home with NeoPower hot water heat pump"
          className="w-full h-full object-cover object-right"
          loading="eager"
          fetchpriority="high"
        />
        {/* Left-side dark gradient for headline legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/85 to-transparent" />
        {/* Soft bottom fade on mobile so trust strip blends nicely */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0B132B]/70 to-transparent lg:hidden" />
      </div>

      {/* Soft brand accent glow */}
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#10B981]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-24 pb-20 sm:pt-32 sm:pb-36 grid lg:grid-cols-12 gap-10 items-center lg:min-h-[680px]">
        <Reveal className="lg:col-span-7 text-white">
          <span className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/8 backdrop-blur border border-white/15 text-[11px] font-semibold tracking-[0.22em] uppercase text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-dot" />
            Home Electrification · Zero Carbon Homes
          </span>

          <h1 className="font-heading mt-7 text-[42px] leading-[1.02] sm:text-6xl lg:text-[80px] font-bold tracking-tight max-w-3xl">
            <span className="text-white">Aero</span>
            <span className="text-[#10B981]">Max</span>
            <span className="text-white"> Energy.</span>
            <br />
            <span className="text-[#10B981]">Powering</span>
            <span className="text-white"> Your Hot Water.</span>
          </h1>

          <p className="mt-6 text-base sm:text-xl text-white/80 leading-[1.55] max-w-xl font-light">
            Premium hot water heat pumps for modern Kiwi homes — designed to lower running costs, reduce emissions and deliver reliable hot water year-round.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/savings-calculator"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-8 py-4 font-semibold text-base transition-all hover:shadow-xl hover:shadow-emerald-500/40 active:scale-95"
            >
              Calculate Your Potential Savings
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/neopower-black-shield"
              className="inline-flex items-center gap-2 rounded-full border border-white/35 text-white hover:bg-white hover:text-[#0B132B] px-8 py-4 font-semibold transition-all backdrop-blur-sm"
            >
              View Products
            </Link>
          </div>

          {/* Trust line */}
          <div className="mt-10 pt-8 border-t border-white/15 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70">
            <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#10B981]" /> NZ-owned & operated</div>
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#10B981]" /> Photo-based pre-assessment</div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#10B981] pulse-dot" /> Installing across NZ
            </div>
          </div>
        </Reveal>

        {/* Right column — empty on desktop (background image already shows product). 
            On mobile the product is partly visible via object-right cropping. */}
        <div className="hidden lg:block lg:col-span-5" aria-hidden="true" />
      </div>
    </section>
  );
};
